/**
 * What one indicator is worth right now — spec §F5.9's popover content, extracted so it can
 * be shown in two places.
 *
 * "Big mono value + unit — **sum for count indicators, mean for rates over current scope** —
 * ▲/▼ delta vs previous year (green/red); scope line `{scope} · {year} · {domain}`; target
 * Donut gauge `{pct}% OF TARGET` (hidden if no target); 'Trend 2021–2026' Spark; 'By country
 * · year' BarsV (only when scope=ALL, country palette); buttons `Map this indicator` /
 * `Dashboard`. **Re-renders on country/year changes while open.**"
 *
 * ## Why it is a component of its own now (client review R7)
 *
 * > *"The space freed at the bottom of the left sidebar becomes an Analytics section for the
 * > current indicator+scope … reuse the IndicatorPopover content/components; the floating
 * > popover is no longer the default surface."*
 *
 * The numbers were only ever reachable through a floating panel that opened when you clicked
 * one of six round buttons on the right edge of the map. A reader who mapped an indicator
 * from the sidebar — the ordinary path — got a choropleth and no figure at all. So the same
 * content is mounted twice: pinned in the sidebar for whatever is **mapped** (`SidebarAnalytics`),
 * and floating for whatever the §F5.9 dock has **opened** (`IndicatorPopover`). One component,
 * because two copies of "the programme total for B.2.1" would eventually disagree.
 *
 * Four decisions worth stating, all of them unchanged by the move:
 *
 * 1. **The sum-vs-mean rule is the server's.** §F5.9's "sum for counts, mean for rates" is
 *    §E4 applied one level above admin 0, and the backend already owns it
 *    (`aggregate.programme_value`, exposed as `?level=programme` — see `lib/api.ts`).
 *    Aggregating the four country values in the browser would put a second, divergent
 *    implementation of the programme's headline numbers next to the dashboard's.
 * 2. **The delta comes from the same series the spark draws** — §E4's "value(year) −
 *    value(year−1)" — so the arrow can never contradict the slope above it. In 2021 there
 *    is no previous year and the delta is *omitted*: the mockup printed "▲ 0 vs —", which
 *    asserts "no change" about a comparison that was never made.
 * 3. **The target is the Overview's target, or there is none.** §E4: "'% of target': value /
 *    targets[year][scope] when targets present, else omit gauge (frontend hides)". The
 *    trajectory IFDC delivered on 9 Sep is served by `/indicators/{id}/targets`, and what its
 *    answer *means* — a commitment, the server's apportioning, or the year's own actual
 *    standing in for a commitment that does not exist before 2026 — is `lib/targets.ts`,
 *    which the Overview's "Progress Against Targets" reads through as well. One rule: a panel
 *    and a landing page disagreeing about B.2.1's 2026 target would be two answers to the one
 *    question a reader came with. Only the gauge hides itself when that rule says `none` —
 *    which is every rung **below** a country, because the endpoint takes a country and
 *    nothing finer, and apportioning a commitment down to a commune is the dashboard's own
 *    task, not something to invent in a sidebar; the Target/Progress tile stays and prints
 *    an em dash instead.
 * 4. **Re-rendering on country/year is TanStack Query's job, not an effect's.** Every
 *    number below hangs off a §F1 key containing the country and (where it matters) the
 *    year, so a time-bar tick or a geography change re-reads through the cache. That is also
 *    what makes the sidebar section "update live with year/mode/scope/country" for free.
 */
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { BarsV } from '../../components/charts/BarsV';
import { ChartFrame } from '../../components/charts/ChartFrame';
import { DonutLegend } from '../../components/charts/DonutLegend';
import { chartH, chartW } from '../../components/charts/chartLightboxStore';
import { axisTitle } from '../../components/charts/scale';
import { Donut } from '../../components/charts/Donut';
import { Spark } from '../../components/charts/Spark';
import {
  fetchIndicators,
  fetchProgrammeSeries,
  fetchProgrammeValue,
  fetchSeries,
  fetchTargets,
  fetchUnits,
  fetchValues,
  modeFor,
  queryKeys,
  type UnitsFC,
} from '../../lib/api';
import { fmtNum } from '../../lib/format';
import { Icon } from '../../lib/icons';
import { COUNTRY_COLORS, COUNTRY_ORDER } from '../../lib/palette';
import { resolveTarget } from '../../lib/targets';
import {
  COUNTRY_NAMES,
  effectiveLevels,
  scopeOwnLevel,
  selectYear,
  useAppStore,
} from '../../lib/state';
import { targetPct } from './dock';
import { HierarchyBreakdown } from './HierarchyBreakdown';
import { Block, StatTiles, type StatTile } from './InsightsBlocks';
import { useIndicatorMeta } from './indicatorMeta';
import { SiblingBars } from './SiblingBars';

/** The unspent remainder of the gauge ring — the mockup's `#EEF1EC`. */
const GAUGE_REST = '#EEF1EC';

export interface IndicatorAnalyticsProps {
  indicatorId: string | null;
  /** Spark/bars width — the popover is 292 px wide inside, the sidebar section a little less. */
  width?: number;
  /**
   * §F5.9's "Map this indicator". Pointless in the sidebar section, whose subject *is* the
   * mapped indicator, so the caller decides rather than this file guessing from a variant.
   */
  showMapButton?: boolean;
  /**
   * Whether this panel is on screen — default `true`, which is the popover, whose whole
   * existence is being open. The sidebar section passes `sidebarOpen`, because a collapsed
   * sidebar stays mounted (see `SiblingBars`'s `enabled`). Only the sibling fan-out reads
   * it: the panel's own three queries are one request each and were always paid.
   */
  visible?: boolean;
}

export function IndicatorAnalytics({
  indicatorId,
  width = 292,
  showMapButton = true,
  visible = true,
}: IndicatorAnalyticsProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const setIndicator = useAppStore((s) => s.setIndicator);
  const country = useAppStore((s) => s.country);
  const scope = useAppStore((s) => s.scope);
  const year = useAppStore(selectYear);
  const cumulative = useAppStore((s) => s.cumulative);
  const open = Boolean(indicatorId);

  const { ind, domain, color } = useIndicatorMeta(indicatorId);
  // The same `['indicators']` entry the picker, the time bar and the legend all read — so
  // finding an indicator's siblings costs one `filter` and no request.
  const { data: catalogue } = useQuery({
    queryKey: queryKeys.indicators(),
    queryFn: fetchIndicators,
  });
  // §F5.10's Cumulative checkbox — this reads the same way the map does, so the big number
  // and the choropleth under it are never two different questions.
  const mode = modeFor(cumulative, ind);

  /**
   * §F5.8: under a filter this is about the **filtered unit**, not the programme.
   *
   * "Affects the numbers" is most visible right here — the big mono figure is the one number
   * a reader quotes. `?parent=<scope>&level=<its own level>` returns exactly that unit's row
   * (the subtree filter is inclusive), so there is still no client-side aggregation and no
   * second endpoint: it is the same §E4 answer the choropleth is painting, narrowed to one.
   */
  const scoped = Boolean(scope);
  const { data: headline } = useQuery({
    queryKey: scope
      ? queryKeys.values(indicatorId ?? '', scopeOwnLevel(scope), year, country, mode, scope.unitId)
      : queryKeys.programmeValue(indicatorId ?? '', year, country, mode),
    queryFn: () =>
      scope
        ? fetchValues(indicatorId as string, scopeOwnLevel(scope), year, country, mode, scope.unitId)
        : fetchProgrammeValue(indicatorId as string, year, country, mode),
    enabled: open,
  });
  // The trend, and with it the previous year the delta compares against. Year-independent,
  // so a time-bar tick costs nothing. Under a filter it is §D3's per-unit series — the same
  // one §F5.7's profile spark draws, and therefore the same cache entry.
  const { data: series } = useQuery({
    queryKey: scope
      ? queryKeys.series(indicatorId ?? '', scope.unitId, mode)
      : queryKeys.programmeSeries(indicatorId ?? '', country, mode),
    queryFn: () =>
      scope
        ? fetchSeries(indicatorId as string, scope.unitId, mode)
        : fetchProgrammeSeries(indicatorId as string, country, mode),
    enabled: open,
  });
  // "By country" only exists as a comparison when more than one country is in scope — which
  // a §F5.8 filter never leaves, even for a cross-border watershed whose `country` is null.
  const showBars = country === 'ALL' && !scoped;
  const { data: byCountry } = useQuery({
    queryKey: queryKeys.values(indicatorId ?? '', 0, year, 'ALL', mode),
    queryFn: () => fetchValues(indicatorId as string, 0, year, 'ALL', mode),
    enabled: open && showBars,
  });
  /**
   * IFDC's trajectory for the year — the same cache entry the Overview's country bars read
   * (`queryKeys.targets(id, 0, year, country)`), so a reader who has seen the landing page
   * pays nothing for it here and cannot be shown a different target from the one it drew.
   *
   * Not asked for at all below a country: `/targets` takes `country` and nothing finer, so
   * the answer for a region would be its country's commitment wearing the region's name.
   * Not asked for a survey estimate either — the 2025 MELS round never had a target.
   */
  const { data: targets } = useQuery({
    queryKey: queryKeys.targets(indicatorId ?? '', 0, year, country),
    queryFn: fetchTargets,
    enabled: open && !scoped && Boolean(ind) && ind?.aggregation !== 'none',
  });
  // ISO3 per admin-0 unit id. The map already holds this exact cache entry
  // (`GeographyFocus` fetches it on every load), so this pays nothing for the mapping.
  const { data: adm0 } = useQuery<UnitsFC>({
    queryKey: queryKeys.units('admin', 0, 'ALL', 'z7'),
    queryFn: () => fetchUnits('admin', 0, 'ALL', 'z7'),
    enabled: open && showBars,
  });

  const value = headline?.values[0]?.value ?? null;
  const seriesYears = series?.years ?? [];
  const seriesValues = series?.values ?? [];
  const activeIdx = seriesYears.indexOf(year);
  const prevYear = activeIdx > 0 ? seriesYears[activeIdx - 1] : null;
  const prevValue = activeIdx > 0 ? (seriesValues[activeIdx - 1] ?? null) : null;
  // §E4's "value(year) − value(year − 1)", in whichever reading is on screen: against the
  // previous *annual* value in annual mode, and against the previous *running total* in
  // cumulative mode — where it is therefore the year's own increment, which is the same
  // number the annual mode prints. That is the arithmetic working, not a coincidence.
  const delta = value !== null && prevValue !== null ? value - prevValue : null;

  /** §G8's fixed country order, one bar per programme country. */
  const bars = useMemo(() => {
    const isoByUnit = new Map<string, string>();
    for (const f of adm0?.features ?? []) {
      if (f.properties.country) isoByUnit.set(f.properties.id, f.properties.country);
    }
    const byIso = new Map<string, number>();
    for (const row of byCountry?.values ?? []) {
      const iso = isoByUnit.get(row.unit_id);
      if (iso) byIso.set(iso, row.value);
    }
    return COUNTRY_ORDER.map((iso) => ({
      iso,
      value: byIso.has(iso) ? (byIso.get(iso) as number) : null,
      color: COUNTRY_COLORS[iso],
    }));
  }, [adm0, byCountry]);

  if (!open || !ind) return null;

  // §E4's "% of target", against whatever the trajectory actually asserts for this scope and
  // reading — see `lib/targets.ts`, and `mode` because a year's increment compared against a
  // running total is a progress figure that contradicts the Overview's from the same numbers.
  const resolved = resolveTarget(targets, value, mode);
  const target = resolved.target;
  const pct = targetPct(value, target);

  // The sample behind a survey estimate. It goes on the scope line rather than beside the
  // big number because it qualifies *what the number is of*, which is what that line says —
  // and because "33.8 % n=3,580" read as one string invites the n to be mistaken for part of
  // the figure. Absent for every other kind of indicator, so nothing else gains a suffix.
  const sampleN = headline?.values[0]?.sample_n ?? null;

  const scopeLine = [
    // The filtered unit *is* the scope, and naming the country instead of it would leave the
    // one line that says what the number is of pointing at the wrong place.
    scope ? scope.name : country === 'ALL' ? t('dock.allCountries') : COUNTRY_NAMES[country],
    // The year, and — when the reader is accumulating — what the year *means* here. The
    // scope line is the one place this says what it is counting, and "2026" reading as a
    // whole-programme total without saying so is the confusion this removes.
    mode === 'cumulative' ? t('timebar.toYear', { year }) : String(year),
    // `fmtNum`, not `toLocaleString()`: the latter groups by the *browser's* locale, so a
    // reader on a French page with an English browser would get "n=3,580" beside "33,8 %".
    // §F12's rule is that the interface language is the locale (`lib/format.ts`).
    sampleN === null ? '' : t('analytics.sample', { n: fmtNum(sampleN, 0) }),
    domain?.short_name ?? '',
  ]
    .filter(Boolean)
    .join(' · ');

  /**
   * The headline row — spec §C.3's *"value + unit + scope as a `StatTiles` row (value, target
   * or baseline, progress)"*.
   *
   * It used to be a 30 px mono figure with the delta hung off its baseline and the scope line
   * under it — three type sizes and two alignments before the first chart. Client, 15 Sep:
   * *"very clean and less congested … everything should be clean and professional"*, which
   * this panel had been left out of. The tiles are the same box the raster insights use, so
   * the two halves of the sidebar now read as one instrument.
   *
   * **The lead tile takes the whole row and the rest go two-up**, and the row is arranged so
   * it is never odd: a tile beside an empty half reads as a figure that failed to load. The
   * previous year's figure and the delta arrive together (a delta needs both ends), and
   * target and progress arrive together (progress *is* value ÷ target), so the four secondary
   * tiles are two pairs; `wide` on the last one is the belt-and-braces case where a previous
   * value exists but the current year is unreported.
   *
   * **A baseline sheet has no target**, so it gets neither of that pair: §C's targets are for
   * the contractual and outcome indicators, and "Progress —" under a survey estimate would be
   * inventing a goal the 2025 MELS round never had. Its comparison is the previous survey,
   * which is the same pair of tiles every other indicator gets.
   */
  const survey = ind.aggregation === 'none';
  const yearLabel = survey ? 'analytics.tileEstimate' : 'analytics.tileAchieved';
  /**
   * The Target tile's label **says where the number came from**.
   *
   * A pre-2026 year has no commitment and stands its own achievement in for one, so progress
   * is 100 % by construction; a country's figure is either IFDC's own country allocation
   * (B.2.1 and C.2.1, 6 Oct 2026: "(IFDC allocation)") or, for any other indicator, the
   * programme commitment apportioned by the server, which is not an IFDC allocation. Printing
   * any of these as a bare "Target" would misstate whose number it is — the same reason the
   * Overview labels its bars.
   */
  const targetLabel =
    resolved.kind === 'actuals'
      ? t('analytics.tileTargetActuals', { year })
      : resolved.kind === 'apportioned'
        ? t('analytics.tileTargetApportioned', { year })
        : resolved.kind === 'allocated'
          ? t('analytics.tileTargetAllocated', { year })
          : resolved.kind === 'none'
            ? t('analytics.tileTarget', { year })
            : // A commitment is only a commitment against a stated reading, and §F5.10's
              // checkbox moves that reading under the reader. "Target 2026: 1,500,000" beside a
              // cumulative headline and beside an annual one are two different numbers wearing
              // one label, which is the Overview disagreement this round set out to close.
              t(
                mode === 'cumulative'
                  ? 'analytics.tileTargetCumulative'
                  : 'analytics.tileTargetAnnual',
                { year },
              );
  const secondary: StatTile[] = [];
  if (!survey) {
    secondary.push(
      {
        label: targetLabel,
        text: target === null ? undefined : fmtNum(target, ind.fmt),
        value: target,
        unit: ind.unit ?? undefined,
        extra: { 'data-ipotarget': resolved.kind },
      },
      {
        label: t('analytics.tileProgress'),
        text: pct === null ? undefined : t('analytics.tileProgressValue', { pct }),
        value: pct,
        extra: { 'data-ipoprogress': '' },
      },
    );
  }
  if (prevYear !== null && prevValue !== null) {
    secondary.push({
      // The figure this one is being read against — for a survey estimate, the previous round.
      label: t(yearLabel, { year: prevYear }),
      text: fmtNum(prevValue, ind.fmt),
      unit: ind.unit ?? undefined,
    });
  }
  if (delta !== null) {
    secondary.push({
      // The year the comparison is against is the tile's *label*: it says what the figure is
      // of, which is the job every other label in the grammar does.
      label: t('dock.vsYear', { year: prevYear }),
      text: `${delta >= 0 ? '▲' : '▼'} ${fmtNum(Math.abs(delta), ind.fmt)}`,
      tone: delta >= 0 ? 'up' : 'down',
      extra: { 'data-ipodelta': delta >= 0 ? 'up' : 'down' },
    });
  }
  if (secondary.length % 2 === 1) {
    const last = secondary[secondary.length - 1] as StatTile;
    secondary[secondary.length - 1] = { ...last, wide: true };
  }

  const tiles: StatTile[] = [
    {
      lead: true,
      label: t(yearLabel, { year }),
      text: value === null ? undefined : fmtNum(value, ind.fmt),
      value,
      unit: ind.unit ?? undefined,
      extra: { 'data-ipoval': '' },
    },
    ...secondary,
  ];

  return (
    <div className="ins-body" data-ipobody={ind.id}>
      {/* No title: the panel's own header names the indicator, and repeating it here is the
          congestion the grammar exists to remove. The scope line is the subtitle — it is
          already the sentence that says what the numbers below are *of*. */}
      <section className="ins-block" data-insblock="headline">
        <p className="ins-sub" data-iposcope="">
          {scopeLine}
        </p>
        <StatTiles tiles={tiles} />

        {/* An em-dash where a number should be is the one thing this panel says without
            explaining, and for a survey estimate it is the ordinary state rather than a
            fault: the MELS baseline was estimated at country and watershed, so a region scope
            has nothing and always will. Only shown for `aggregation: 'none'` — for a roll-up
            indicator a missing number means the year is unreported, which is what the time bar
            and the legend already say. */}
        {value === null && ind.aggregation === 'none' ? (
          <p className="ins-note" data-iponovalue="">
            {t('analytics.noValueAtScope')}
          </p>
        ) : null}
      </section>

      {/*
        The gauge, **except where the target is the year's own achievement**. `actuals` means
        no commitment reaches this year, so the ring is pinned at 100 % by construction — a
        full green circle that reports nothing and looks like the best news on the panel. The
        Progress tile stays, where "100 %" sits under a label that says "(actuals)" and is
        read as the qualifier it is rather than as an achievement.
      */}
      {pct === null || resolved.kind === 'actuals' ? null : (
        <Block
          id="progress"
          title={t('analytics.progressTitle')}
          // The subtitle names the target the ring is against — the plain "{year} target"
          // for a commitment, and the tile's own qualified label when the server apportioned
          // it, where an unqualified "2026 target" would read as an IFDC allocation.
          sub={
            resolved.kind === 'apportioned' || resolved.kind === 'allocated'
              ? targetLabel
              : t('analytics.progressSub', { year })
          }
          extra={{ 'data-ipogauge': '' }}
        >
          <div className="ins-donut">
            <ChartFrame
              title={t('analytics.progressTitle')}
              subtitle={ind.name}
              legend={
                <DonutLegend
                  items={[
                    { label: t('dock.achieved'), value: pct, color },
                    { label: t('dock.toTarget'), value: 100 - pct, color: GAUGE_REST },
                  ]}
                  unit="%"
                />
              }
              testId="ipo-gauge"
            >
              {(size) => (
                <Donut
                  items={[
                    { label: t('dock.achieved'), value: pct, color },
                    { label: t('dock.toTarget'), value: 100 - pct, color: GAUGE_REST },
                  ]}
                  size={size.expanded ? Math.round(Math.min(size.w, size.h) * 0.62) : 86}
                  centerLabel={`${pct}%`}
                  centerSub={t('dock.ofTarget')}
                  fmt={0}
                />
              )}
            </ChartFrame>
          </div>
        </Block>
      )}

      {/*
        **A series with no point to plot has no trend**, and the block goes rather than
        printing a heading over an empty chart.

        `/indicators/{id}/series?scope=programme` answers with the *programme's* year axis —
        2021 to 2026 — and a null per year for a series that reports none of them. For a
        contractual indicator that is a gap in reporting and the spark shows it; for a 2025
        survey estimate the survey did not compute at this scope it is permanent, and
        "Trend 2021–2026" over blank axes asserts a six-year range for a one-year measurement.
        That is the same lie `InsightsPanel` refuses when it drops a trend a dataset does not
        have, and since the 8 Sep directive ten rail chips lead straight here, so the state
        stopped being rare. The headline's own em-dash and its `data-iponovalue` sentence are
        what say there is no number; a chart saying it a second time, in axes, is not a
        second answer.
      */}
      {seriesValues.some((value) => value !== null && value !== undefined) ? (
        <Block
          id="trend"
          title={t('dock.trend', {
            from: seriesYears[0] ?? 2021,
            to: seriesYears[seriesYears.length - 1] ?? 2026,
          })}
          titleExtra={{ 'data-ipotrend': '' }}
        >
          <Spark
            series={seriesValues}
            w={width}
            h={62}
            color={color}
            unit={ind.unit}
            fmt={ind.fmt}
            activeIdx={activeIdx}
            labels={seriesYears}
            ariaLabel={t('dock.trendAria', { indicator: ind.name })}
          />
        </Block>
      ) : null}

      {/* P5(3): where this sub-indicator sits among the others from the same workbook sheet.
          Under the trend rather than above it — the trend is about *this* series over time,
          which is the panel's subject; the siblings are context for the current figure. */}
      <SiblingBars ind={ind} catalogue={catalogue ?? []} color={color} enabled={visible} />

      {/*
        §F5.9's own "By country · year" bars — kept, and kept **only** for the unfiltered
        programme view they were written for. §G8's fixed four colours and the ISO labels are
        the shape a reader already knows from the dashboard, so the programme extent keeps it
        verbatim; every rung below it is the general breakdown under this, which is the block
        the 9 Sep directive adds.
      */}
      {showBars ? (
        <Block
          id="bycountry"
          title={t('dock.byCountry', { year })}
          titleExtra={{ 'data-ipobarstitle': '' }}
        >
          <ChartFrame
            title={t('dock.byCountry', { year })}
            subtitle={ind.name}
            xLabel={t('charts.axis.country')}
            yLabel={axisTitle(t('analysis.axis.indicatorValue'), ind.unit)}
            testId="ipo-bycountry"
          >
            {(size) => (
              <BarsV
                labels={bars.map((b) => b.iso)}
                values={bars.map((b) => b.value)}
                colors={bars.map((b) => b.color)}
                w={chartW(size, 290)}
                h={chartH(size, 104)}
                unit={ind.unit}
                fmt={ind.fmt}
                ariaLabel={t('dock.byCountryAria', { indicator: ind.name, year })}
              />
            )}
          </ChartFrame>
        </Block>
      ) : null}

      {/*
        The per-hierarchy breakdown — the donut and the bars for the rung *below* the current
        filter, for both indicator families. At the programme extent the bars above already
        are the country comparison, so only the ring is drawn there; under any filter this is
        the block that says where inside it the numbers come from. See `HierarchyBreakdown`.
      */}
      <HierarchyBreakdown
        ind={ind}
        value={value}
        color={color}
        width={width}
        enabled={visible}
        showBars={!showBars}
      />

      <div className="ins-actions">
        {showMapButton ? (
          <button
            type="button"
            className="btn primary sm"
            data-ipomap=""
            // The dock button for a demo-only indicator is already dead, so this should be
            // unreachable for one — but `setDockInd` is public, and "map it" is the one
            // control here that would put simulated numbers on the map.
            disabled={!ind.active}
            title={!ind.active ? t('indicators.inactiveTitle') : undefined}
            onClick={() => setIndicator(ind.id, ind.has_watershed_values, effectiveLevels(ind))}
          >
            <Icon name="map" size={12} /> {t('dock.mapThis')}
          </button>
        ) : null}
        <button
          type="button"
          className="btn ghost sm"
          data-ipodash=""
          onClick={() => void navigate('/dashboard')}
        >
          <Icon name="chart" size={12} /> {t('dock.dashboard')}
        </button>
      </div>
    </div>
  );
}

export default IndicatorAnalytics;
